import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Accordion, Alert, Button, Icon, Modal } from '@seeqdev/qomponents';
import { sqAdministrationStore, sqAiAssistantStore, sqWorkbenchStore } from '@/core/core.stores';
import { refreshAdminContactInfo } from '@/administration/administration.actions';
import { toBase64 } from '@/utilities/utilities';
import { FormElement } from '@/formbuilder/formBuilder.constants';
import { FrequencyEnum, SeverityEnum, SupportRequestInputV1 } from '@/sdk/model/SupportRequestInputV1';
import { AttachmentInputV1 } from '@/sdk/model/AttachmentInputV1';
import { SimpleSaveFormBuilder } from '@/formbuilder/SimpleSaveFormBuilder.page';
import { sqLogsApi } from '@/sdk/api/LogsApi';
import { successToast } from '@/utilities/toast.utilities';
import { extractError } from '@/requests/axios.utilities';
import { setAIAssistantDisplayed, setAIAssistantExternalPrompt } from '@/aiAssistant/aiAssistant.actions';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { doTrack } from '@/track/track.service';

enum ReasonOption {
  Problem = 'Problem',
  Feature = 'Feature',
  Other = 'Other',
}

interface SupportRequestDetails {
  reason: keyof typeof ReasonOption;
  summary: string;
  description: string;
}

interface SupportRequestModalProps {
  onClose: () => void;
  defaultActiveKey?: '0' | '1';
  details?: Partial<SupportRequestDetails>;
}

const TabHeader: React.FunctionComponent<{
  label: string;
  icon: string;
  isExpanded: boolean;
  extraClassnames?: string;
}> = ({ label, icon, isExpanded, extraClassnames = '' }) => (
  <h3 className={`cursorPointer m0 text-left flexlayout__tabset_tabbar_outer flexAlignCenter p10 ${extraClassnames}`}>
    <Icon icon={icon} extraClassNames="mr5" />
    {label}
    <Icon icon={isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} extraClassNames="ml10" small={true} type="text" />
  </h3>
);

export const SupportRequestModal: React.FunctionComponent<SupportRequestModalProps> = ({
  onClose,
  defaultActiveKey = '0',
  details = {},
}) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState('');
  const [name, setName] = useState(
    `${sqWorkbenchStore.currentUser.firstName ?? ''} ${sqWorkbenchStore.currentUser.lastName ?? ''}`.trim(),
  );
  const [email, setEmail] = useState(sqWorkbenchStore.currentUser.email);
  const [summary, setSummary] = useState(details.summary ?? '');
  const [description, setDescription] = useState(details.description ?? '');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [reason, setReason] = useState(details.reason ? ReasonOption[details.reason] : undefined);
  const [frequency, setFrequency] = useState(FrequencyEnum.NotApplicable);
  const [severity, setSeverity] = useState(SeverityEnum.S4);
  const [file, setFile] = useState<File>();
  const adminContactInfo = useFluxPath(sqAdministrationStore, () => sqAdministrationStore.adminContactInfo);
  useEffect(() => {
    if (!adminContactInfo.name || !adminContactInfo.email) {
      refreshAdminContactInfo();
    }
  }, []); // Runs only on mount
  const isAiAssistantDisplayed = useFluxPath(sqAiAssistantStore, () => sqAiAssistantStore.displayed);
  const reasonOptions = [
    { label: 'SUPPORT_REQUEST.REASON_OPTIONS.PROBLEM', value: ReasonOption.Problem },
    { label: 'SUPPORT_REQUEST.REASON_OPTIONS.FEATURE', value: ReasonOption.Feature },
    { label: 'SUPPORT_REQUEST.REASON_OPTIONS.OTHER', value: ReasonOption.Other },
  ];
  const descriptionPlaceholders = {
    [ReasonOption.Problem]: 'SUPPORT_REQUEST.OBSERVED_PLACEHOLDER',
    [ReasonOption.Feature]: 'SUPPORT_REQUEST.FEATURE_PLACEHOLDER',
    [ReasonOption.Other]: 'SUPPORT_REQUEST.DESCRIPTION_PLACEHOLDER',
  };
  const frequencyOptions = [
    { label: t('SUPPORT_REQUEST.FREQUENCY_OPTIONS.NA'), value: FrequencyEnum.NotApplicable },
    { label: t('SUPPORT_REQUEST.FREQUENCY_OPTIONS.ONCE'), value: FrequencyEnum.Once },
    { label: t('SUPPORT_REQUEST.FREQUENCY_OPTIONS.INFREQUENTLY'), value: FrequencyEnum.Infrequently },
    { label: t('SUPPORT_REQUEST.FREQUENCY_OPTIONS.EVERY_TIME'), value: FrequencyEnum.EveryTime },
  ];
  const severityOptions = [
    { label: t('SUPPORT_REQUEST.SEVERITY_OPTIONS.S4'), value: SeverityEnum.S4 },
    { label: t('SUPPORT_REQUEST.SEVERITY_OPTIONS.S3'), value: SeverityEnum.S3 },
    { label: t('SUPPORT_REQUEST.SEVERITY_OPTIONS.S2'), value: SeverityEnum.S2 },
    { label: t('SUPPORT_REQUEST.SEVERITY_OPTIONS.S1'), value: SeverityEnum.S1 },
  ];

  const closeModal = () => {
    onClose();
  };

  const [option, setOption] = useState<'0' | '1'>(defaultActiveKey || '0');

  const sendSupportRequest = async () => {
    let attachment: AttachmentInputV1 | undefined;
    if (file) {
      const content = await toBase64(file);
      attachment = { content, filename: file.name, type: file.type };
    }

    setErrorMessage('');
    const supportRequest: SupportRequestInputV1 = {
      name,
      email,
      summary,
      description,
      frequency,
      severity,
      stepsToReproduce,
      attachment,
    };
    try {
      const response = await sqLogsApi.send(supportRequest);
      successToast({
        messageKey: 'SUPPORT_REQUEST.SENT',
        buttonLabelKey: 'SUPPORT_REQUEST.VIEW_REQUEST',
        buttonAction: () => window.open(response.data.issueUrl),
        buttonIcon: 'fa-arrow-up-right-from-square',
      });
      closeModal();
    } catch (e) {
      setErrorMessage(extractError(e).message);
    }
  };

  const isReasonSelected = !_.isUndefined(reason);
  const formDefinition: FormElement[] = [
    {
      component: 'FormRow',
      name: 'nameAndEmailRow',
      extraClassNames: 'flexSpaceBetween',
      components: [
        {
          component: 'TextFieldFormComponent',
          name: 'name',
          label: 'USER.NAME',
          value: name,
          required: true,
          onChange: (value) => setName(value as string),
          size: 'sm',
          testId: 'name',
          wrapperClassNames: 'flexBasis100Percent',
        },
        {
          component: 'TextFieldFormComponent',
          name: 'email',
          label: 'USER.EMAIL',
          value: email,
          required: true,
          onChange: (value) => setEmail(value as string),
          size: 'sm',
          testId: 'email',
          wrapperClassNames: 'flexBasis100Percent ml10',
        },
      ],
    },
    {
      component: 'TextFieldFormComponent',
      name: 'summary',
      label: 'SUPPORT_REQUEST.SUMMARY',
      placeholder: 'SUPPORT_REQUEST.SUMMARY_PLACEHOLDER',
      value: summary,
      required: true,
      onChange: (value) => setSummary(value as string),
      size: 'sm',
      testId: 'requestSummary',
    },
    {
      component: 'SelectFormComponent',
      name: 'selectReason',
      label: 'SUPPORT_REQUEST.REASON',
      value: reason?.toString() || '',
      onChange: (value) => setReason(ReasonOption[value as keyof typeof ReasonOption]),
      options: reasonOptions,
      required: true,
      insideModal: true,
      testId: 'selectReason',
    },
    {
      component: 'SelectFormComponent',
      includeIf: reason === ReasonOption.Problem,
      name: 'selectSeverity',
      label: 'SUPPORT_REQUEST.SEVERITY',
      value: severity.toString(),
      onChange: (value) => setSeverity(SeverityEnum[value as keyof typeof SeverityEnum]),
      options: severityOptions,
      required: true,
      insideModal: true,
    },
    {
      component: 'FormRow',
      includeIf: isReasonSelected,
      name: 'frequencyAndTimeRow',
      extraClassNames: 'flexSpaceBetween mb8',
      components: [
        {
          component: 'SelectFormComponent',
          includeIf: reason === ReasonOption.Problem,
          name: 'selectFrequency',
          label: 'SUPPORT_REQUEST.FREQUENCY',
          value: frequency.toString(),
          onChange: (value) => setFrequency(FrequencyEnum[value as keyof typeof FrequencyEnum]),
          options: frequencyOptions,
          required: true,
          insideModal: true,
          wrapperClassNames: 'flexBasis100Percent mr10',
        },
        {
          component: 'FileDropperFormComponent',
          value: file?.name,
          filename: file?.name,
          name: 'fileUploadInput',
          id: 'fileUploadInput',
          text: 'SUPPORT_REQUEST.SCREENSHOT',
          actionText: 'SUPPORT_REQUEST.SCREENSHOT_ATTACHED',
          fileExtension: '.png,.jpg,.gif,.webp',
          fileDropCallback: async (file) => setFile(file),
          clearFileCallback: () => {
            setFile(undefined);
          },
          extraClassNames: 'flexBasis100Percent',
          validation: () => false,
        },
      ],
    },
    {
      component: 'TextFieldFormComponent',
      includeIf: isReasonSelected,
      name: 'description',
      label: reason === ReasonOption.Problem ? 'SUPPORT_REQUEST.OBSERVED' : 'SUPPORT_REQUEST.DESCRIPTION',
      placeholder: isReasonSelected ? descriptionPlaceholders[reason] : '',
      value: description,
      required: true,
      onChange: (value) => setDescription(value as string),
      size: 'sm',
      testId: 'requestDescription',
      as: 'textarea',
      rows: 2,
    },
    {
      component: 'TextFieldFormComponent',
      includeIf: reason === ReasonOption.Problem,
      name: 'stepsToReproduce',
      label: 'SUPPORT_REQUEST.STEPS_TO_REPRODUCE',
      placeholder: 'SUPPORT_REQUEST.STEPS_TO_REPRODUCE_PLACEHOLDER',
      value: stepsToReproduce,
      required: false,
      onChange: (value) => setStepsToReproduce(value as string),
      size: 'sm',
      testId: 'requestStepsToReproduce',
      as: 'textarea',
      rows: 2,
    },
  ];

  return (
    <Modal
      open={true}
      onClose={closeModal}
      onPointerDownOutside={closeModal}
      testId="supportRequestModal"
      title={t('SUPPORT_REQUEST.GET_HELP')}
      hideFooterButtons={true}>
      <div className="p24">
        <Accordion
          value={option}
          onItemSelect={(value: string) => setOption(value as '0' | '1')}
          extraClassNames="lightGreyBorder"
          accordionItems={[
            {
              value: '0',
              trigger: (
                <TabHeader
                  label={t('SUPPORT_REQUEST.HELP_YOURSELF')}
                  icon="fa-search"
                  isExpanded={option === '0'}
                  extraClassnames="btlr15 btrr15"
                />
              ),
              itemTestId: 'search-kb-help',
              id: '0',
              content: (
                <div className="text-center p10 pb0">
                  <p className="mt0 mb10">
                    <em>{t('SUPPORT_REQUEST.SEARCH_KB_HELP')}</em>
                  </p>
                  <div className="flexColumnContainer flexCenter">
                    <a href="https://support.seeq.com/" target="_blank" rel="noopener">
                      <Button
                        icon="fa-arrow-up-right-from-square"
                        extraClassNames="mr-2"
                        variant="theme"
                        label={t('SUPPORT_REQUEST.SEARCH_KB_BUTTON')}
                      />
                    </a>
                    {t('OR')}
                    <Button
                      icon="fa fa-sparkles"
                      extraClassNames="ml-2"
                      variant="theme"
                      label={t('SUPPORT_REQUEST.SEARCH_AI_BUTTON')}
                      onClick={() => {
                        doTrack('AiAssistant', 'open from Support Request Modal');
                        if (!isAiAssistantDisplayed) {
                          setAIAssistantDisplayed(true);
                        }
                        setAIAssistantExternalPrompt({ prompt: t('AI_ASSISTANT.HELP_PROMPT'), agent: 'general' });
                        closeModal();
                      }}
                    />
                  </div>
                  <p className="mt20">
                    {t('SUPPORT_REQUEST.ADMIN_CONTACT')}:{' '}
                    <a href={`mailto:${adminContactInfo.email}`}>{adminContactInfo.name}</a>
                  </p>
                </div>
              ),
            },
            {
              value: '1',
              trigger: (
                <TabHeader
                  label={t('SUPPORT_REQUEST.LET_US_HELP')}
                  icon="fa-life-ring"
                  isExpanded={option === '1'}
                  extraClassnames={option !== '1' ? 'bblr15 bbrr15' : ''}
                />
              ),
              itemTestId: 'let-us-help',
              id: '1',
              content: (
                <div className="p10">
                  {errorMessage && (
                    <Alert extraClassNames="p10" variant="danger" dismissible={false}>
                      {errorMessage}
                      <a
                        href="https://seeq.atlassian.net/servicedesk/customer/portal/3"
                        target="_blank"
                        className="displayBlock mt8"
                        rel="noopener">
                        {t('SUPPORT_REQUEST.OPEN_PORTAL')}
                      </a>
                    </Alert>
                  )}
                  <SimpleSaveFormBuilder
                    formDefinition={formDefinition}
                    submitFn={sendSupportRequest}
                    closeFn={closeModal}
                    submitBtnLabel="SUPPORT_REQUEST.SEND"
                  />
                </div>
              ),
            },
          ]}
        />
      </div>
    </Modal>
  );
};
